import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Paper,
  alpha,
  useTheme,
  Stack,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Icon } from "@mui/material";

interface CashPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (amountPaid: number) => void;
  totalAmount: number;
}

export const CashPaymentDialog: React.FC<CashPaymentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  totalAmount,
}) => {
  const theme = useTheme();
  const [amountPaid, setAmountPaid] = React.useState<string>("");
  const change = React.useMemo(() => {
    const paid = parseFloat(amountPaid) || 0;
    return Math.max(0, paid - totalAmount);
  }, [amountPaid, totalAmount]);

  // Generate quick amount suggestions based on total
  const quickAmounts = React.useMemo(() => {
    const ceiling = Math.ceil(totalAmount);
    const suggestions = [ceiling];

    const nearestFive = Math.ceil(totalAmount / 5) * 5;
    const nearestTen = Math.ceil(totalAmount / 10) * 10;

    if (nearestFive !== ceiling) suggestions.push(nearestFive);
    if (nearestTen !== nearestFive) suggestions.push(nearestTen);

    if (totalAmount < 10) {
      suggestions.push(10);
      suggestions.push(20);
    } else if (totalAmount < 20) {
      suggestions.push(20);
      suggestions.push(50);
    } else {
      suggestions.push(nearestTen + 10);
      suggestions.push(nearestTen + 20);
    }

    return [...new Set(suggestions)].sort((a, b) => a - b).slice(0, 5);
  }, [totalAmount]);

  const handleQuickAmount = (amount: number) => {
    setAmountPaid(amount.toFixed(3));
  };

  const handleNumericInput = (value: string) => {
    if (value === "." && amountPaid.includes(".")) return;
    if (value === "." && amountPaid === "") {
      setAmountPaid("0.");
      return;
    }
    if (value === "backspace") {
      setAmountPaid((prev) => prev.slice(0, -1));
      return;
    }
    if (value === "clear") {
      setAmountPaid("");
      return;
    }

    const newAmount = amountPaid + value;
    const [whole, decimal] = newAmount.split(".");
    if (decimal && decimal.length > 3) return;
    setAmountPaid(newAmount);
  };

  const handleManualInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9.]/g, "");
    if (value === "") {
      setAmountPaid("");
      return;
    }
    const [whole, decimal] = value.split(".");
    if (decimal && decimal.length > 3) return;
    if (value.split(".").length > 2) return;
    setAmountPaid(value);
  };

  const handleConfirm = () => {
    const paid = parseFloat(amountPaid);
    if (paid >= totalAmount) {
      onConfirm(paid);
    }
  };

  const handleClose = () => {
    setAmountPaid("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: "blur(10px)",
          overflow: "hidden",
          minWidth: 900,
        },
      }}
    >
      <Box sx={{ display: "flex", minHeight: 560 }}>
        {/* Left Column - Payment Info */}
        <Box
          sx={{
            width: 380,
            backgroundColor: "#1E8078",
            color: "white",
            p: 4,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: 24,
              background:
                "linear-gradient(to left, rgba(0,0,0,0.1), transparent)",
            },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="h6"
                sx={{ opacity: 0.9, mb: 1, letterSpacing: 0.5 }}
              >
                Amount Due
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                BHD {totalAmount.toFixed(3)}
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: alpha("#fff", 0.1),
                borderRadius: 3,
                p: 3,
                mb: 4,
              }}
            >
              <Typography
                variant="h6"
                sx={{ opacity: 0.9, mb: 2, letterSpacing: 0.5 }}
              >
                Amount Received
              </Typography>
              <TextField
                fullWidth
                value={amountPaid}
                onChange={handleManualInput}
                variant="outlined"
                placeholder="0.000"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: "white", fontWeight: 500 }}>
                        BHD
                      </Typography>
                    </InputAdornment>
                  ),
                  sx: {
                    color: "white",
                    fontSize: "2rem",
                    fontWeight: 700,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.2)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.3)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.5)",
                    },
                    caretColor: "white",
                  },
                }}
              />
            </Box>

            {/* Change Display */}
            <Box
              sx={{
                backgroundColor: alpha(
                  change > 0 ? "#fff" : "rgba(255,255,255,0.1)",
                  0.1
                ),
                p: 3,
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography sx={{ opacity: 0.9, letterSpacing: 0.5 }}>
                  Change Due
                </Typography>
                <Icon sx={{ fontSize: 28, opacity: 0.8 }}>
                  {change > 0 ? "payments" : "pending"}
                </Icon>
              </Stack>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                BHD {change.toFixed(3)}
              </Typography>
            </Box>
          </Box>

          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              mt: 4,
              borderColor: "rgba(255,255,255,0.3)",
              color: "white",
              height: 48,
              borderRadius: 2,
              "&:hover": {
                borderColor: "white",
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Cancel
          </Button>
        </Box>

        {/* Right Column - Keypad */}
        <Box sx={{ flex: 1, p: 4 }}>
          {/* Quick Amount Buttons */}
          <Typography
            variant="h6"
            sx={{ mb: 2.5, fontWeight: 600, color: "#1E8078" }}
          >
            Quick Amounts
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 4 }}>
            {quickAmounts.map((amount) => (
              <Grid item xs={12 / quickAmounts.length} key={amount}>
                <Button
                  fullWidth
                  variant={
                    amountPaid === amount.toFixed(3) ? "contained" : "outlined"
                  }
                  onClick={() => handleQuickAmount(amount)}
                  sx={{
                    height: 52,
                    borderColor: alpha("#1E8078", 0.3),
                    color:
                      amountPaid === amount.toFixed(3) ? "white" : "#1E8078",
                    backgroundColor:
                      amountPaid === amount.toFixed(3)
                        ? "#1E8078"
                        : "transparent",
                    "&:hover": {
                      backgroundColor:
                        amountPaid === amount.toFixed(3)
                          ? "#1E8078"
                          : alpha("#1E8078", 0.05),
                      borderColor: "#1E8078",
                    },
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  {amount} BD
                </Button>
              </Grid>
            ))}
          </Grid>

          {/* Numeric Keypad */}
          <Typography
            variant="h6"
            sx={{ mb: 2.5, fontWeight: 600, color: "#1E8078" }}
          >
            Enter Amount
          </Typography>
          <Grid container spacing={2}>
            {[7, 8, 9, 4, 5, 6, 1, 2, 3, ".", 0, "backspace"].map((key) => (
              <Grid item xs={4} key={key}>
                <Paper
                  elevation={0}
                  onClick={() => handleNumericInput(key.toString())}
                  sx={{
                    height: 72,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    backgroundColor: alpha("#1E8078", 0.05),
                    transition: "all 0.2s",
                    borderRadius: 2,
                    border: `1px solid ${alpha("#1E8078", 0.1)}`,
                    "&:hover": {
                      backgroundColor: alpha("#1E8078", 0.1),
                      border: `1px solid ${alpha("#1E8078", 0.2)}`,
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                    },
                  }}
                >
                  {key === "backspace" ? (
                    <Icon sx={{ color: "#1E8078", fontSize: 32 }}>
                      backspace
                    </Icon>
                  ) : (
                    <Typography
                      variant="h4"
                      sx={{ color: "#1E8078", fontWeight: 600 }}
                    >
                      {key}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Clear and Confirm Buttons */}
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleNumericInput("clear")}
                sx={{
                  height: 56,
                  borderColor: alpha(theme.palette.error.main, 0.3),
                  color: theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.error.main, 0.05),
                    borderColor: theme.palette.error.main,
                  },
                  borderRadius: 2,
                  fontSize: "1rem",
                  fontWeight: 500,
                }}
              >
                Clear
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleConfirm}
                disabled={parseFloat(amountPaid) < totalAmount}
                sx={{
                  height: 56,
                  backgroundColor: "#1E8078",
                  "&:hover": {
                    backgroundColor: "#166F68",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: alpha("#1E8078", 0.4),
                  },
                  borderRadius: 2,
                  fontSize: "1rem",
                  fontWeight: 500,
                }}
              >
                Confirm Payment
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Dialog>
  );
};
